import { Button, Layout, Space, Tag } from 'antd';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ConfettiAnimation } from '../../components/ConfettiAnimation';
import {
    BackToModesLink,
    CityNameWithCoatOfArms,
    FinishedPanel,
    Header,
    Page,
    Question,
    QuizPanel,
    StreakStatus,
    pickRandom,
} from '../../components/quiz';
import czechCities from '../../data/cr.mjs';
import populationData from '../../data/populations.json';

const { Content } = Layout;

const MIN_POPULATION = 1000;

const POPULATIONS = populationData.populations;

const CITY_POOL = czechCities
    .map(city => {
        const population = POPULATIONS[String(city.kod)];
        if (!population || population < MIN_POPULATION) {
            return null;
        }
        return {
            kod: String(city.kod),
            obec: city.obec,
            kraj: city.kraj,
            okres: city.okres,
            coatOfArms: city.coatOfArms,
            population,
        };
    })
    .filter(Boolean);

const formatPopulation = value => value.toLocaleString('cs-CZ').replace(/ /g, ' ');

const ConfettiOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 10;

    & > div {
        width: 100% !important;
        height: 100% !important;
    }
`;

const PanelWrap = styled.div`
    position: relative;
`;

const Versus = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 18px;
    align-items: stretch;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 12px;
    }
`;

const VsBadge = styled.div`
    align-self: center;
    justify-self: center;
    font-weight: 700;
    font-size: 18px;
    color: #c80707;
    letter-spacing: 1px;

    @media (max-width: 640px) {
        padding: 4px 0;
    }
`;

const CityCard = styled.button`
    text-align: center;
    background: ${({ $state }) => ($state === 'correct' ? '#f6ffed' : $state === 'wrong' ? '#fff1f0' : '#ffffff')};
    border: 2px solid ${({ $state }) => ($state === 'correct' ? '#52c41a' : $state === 'wrong' ? '#ff4d4f' : '#d9d9d9')};
    border-radius: 10px;
    padding: 22px 18px;
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
    transition: transform 0.12s ease, border-color 0.2s ease, background 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    font-family: inherit;
    font-size: inherit;
    color: inherit;

    &:hover {
        transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-2px)')};
        border-color: ${({ $state, $disabled }) => ($disabled || $state ? undefined : '#c80707')};
    }

    &:focus-visible {
        outline: 3px solid #ffd6d6;
        outline-offset: 2px;
    }
`;

const CityName = styled.div`
    font-size: clamp(22px, 3.5vw, 32px);
    font-weight: 700;
    line-height: 1.1;
`;

const CityRegion = styled.div`
    font-size: 14px;
    color: #595959;
`;

const PopulationLabel = styled.div`
    font-size: 28px;
    font-weight: 700;
    color: #1f1f1f;
`;

const pickPair = previous => {
    const a = pickRandom(CITY_POOL);
    let b = pickRandom(CITY_POOL);
    let attempts = 0;
    while (
        attempts < 50 &&
        (b.kod === a.kod ||
            b.population === a.population ||
            (previous && (b.kod === previous.a.kod || b.kod === previous.b.kod)))
    ) {
        b = pickRandom(CITY_POOL);
        attempts++;
    }
    return { a, b };
};

const BiggerCityGame = () => {
    const [pair, setPair] = useState(() => pickPair(null));
    const [pickedKod, setPickedKod] = useState(null);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [confettiKey, setConfettiKey] = useState(0); // forces re-mount of ConfettiAnimation per correct pick

    const reveal = pickedKod !== null;
    const winnerKod = pair.a.population >= pair.b.population ? pair.a.kod : pair.b.kod;
    const isCorrectPick = reveal && pickedKod === winnerKod;

    const handlePick = useCallback(
        city => {
            if (reveal) {
                return;
            }
            const correct = city.kod === winnerKod;
            setPickedKod(city.kod);
            if (correct) {
                setConfettiKey(k => k + 1);
                setStreak(prev => {
                    const next = prev + 1;
                    setBestStreak(best => Math.max(best, next));
                    return next;
                });
            } else {
                setBestStreak(best => Math.max(best, streak));
                setGameOver(true);
            }
        },
        [reveal, winnerKod, streak],
    );

    const advance = () => {
        setPair(previous => pickPair(previous));
        setPickedKod(null);
    };

    const restart = () => {
        setStreak(0);
        setGameOver(false);
        setPickedKod(null);
        setPair(pickPair(null));
    };

    if (gameOver) {
        return (
            <QuizPanel>
                <StreakStatus
                    streak={streak}
                    bestStreak={bestStreak}
                    poolSize={CITY_POOL.length}
                    minPopulation={MIN_POPULATION}
                />
                <FinishedPanel>
                    <Tag color="red">Konec hry</Tag>
                    <Question>
                        <h2>Streak: {streak}</h2>
                        <p>
                            Nejdelší streak v této partii: <strong>{bestStreak}</strong>. Klikni na tlačítko a začni
                            znovu.
                        </p>
                    </Question>
                    <Space wrap>
                        <Button type="primary" size="large" onClick={restart}>
                            Hrát znovu
                        </Button>
                        <Link to="/">
                            <Button size="large">Zpět na výběr</Button>
                        </Link>
                    </Space>
                </FinishedPanel>
            </QuizPanel>
        );
    }

    const cardState = city => {
        if (!reveal) {
            return null;
        }
        if (city.kod === winnerKod) {
            return 'correct';
        }
        if (city.kod === pickedKod) {
            return 'wrong';
        }
        return null;
    };

    return (
        <PanelWrap>
            {isCorrectPick && (
                <ConfettiOverlay key={confettiKey}>
                    <ConfettiAnimation />
                </ConfettiOverlay>
            )}
            <QuizPanel>
                <StreakStatus
                    streak={streak}
                    bestStreak={bestStreak}
                    poolSize={CITY_POOL.length}
                    minPopulation={MIN_POPULATION}
                />
                <Question>
                    <p>Streak hra</p>
                    <h2>Která obec má víc obyvatel?</h2>
                    <p>Klikni na tu, která má podle tebe víc obyvatel. Stačí jediná chyba a hra končí.</p>
                </Question>
                <Versus>
                    <CityCard
                        type="button"
                        $state={cardState(pair.a)}
                        $disabled={reveal}
                        onClick={() => handlePick(pair.a)}
                    >
                        <CityName>
                            <CityNameWithCoatOfArms city={pair.a} size={56} />
                        </CityName>
                        <CityRegion>{pair.a.kraj}</CityRegion>
                        {reveal && <PopulationLabel>{formatPopulation(pair.a.population)}</PopulationLabel>}
                    </CityCard>
                    <VsBadge>VS</VsBadge>
                    <CityCard
                        type="button"
                        $state={cardState(pair.b)}
                        $disabled={reveal}
                        onClick={() => handlePick(pair.b)}
                    >
                        <CityName>
                            <CityNameWithCoatOfArms city={pair.b} size={56} />
                        </CityName>
                        <CityRegion>{pair.b.kraj}</CityRegion>
                        {reveal && <PopulationLabel>{formatPopulation(pair.b.population)}</PopulationLabel>}
                    </CityCard>
                </Versus>
                {reveal && (
                    <Space>
                        <Button type="primary" size="large" onClick={advance}>
                            Další kolo
                        </Button>
                    </Space>
                )}
            </QuizPanel>
        </PanelWrap>
    );
};

export const BiggerCity = () => (
    <Content>
        <Page>
            <Header>
                <BackToModesLink />
                <h1>Co je větší?</h1>
                <p>
                    Jednoduchá hra na intuici. Dostaneš dvě české obce a klikneš na tu, která má víc obyvatel. Sbíráš
                    streak — jediná špatná odpověď znamená konec. Data ČSÚ k 31. 12. 2023 (Praha k 1. 1. 2025).
                </p>
            </Header>
            <BiggerCityGame />
        </Page>
    </Content>
);
